import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

/**
 * GSAP scroll animations for #works (works page + /landing continuation).
 * Extracted from main.js so landing can reuse without importing the full app bundle.
 */
export function revealShowcaseContent(showcase) {
  if (!showcase) return;
  showcase.querySelectorAll('.works-usecase-section').forEach((section) => {
    section.classList.add('is-active');
  });
  showcase.querySelectorAll('.works-editorial-card').forEach((card) => {
    card.classList.add('is-visible');
  });
  showcase
    .querySelectorAll(
      '.works-usecase-title, .works-usecase-heading, .works-usecase-intro, .expandable-card-trigger, .works-cta-section',
    )
    .forEach((el) => gsap.set(el, { opacity: 1, y: 0, clearProps: 'all' }));
}

export function initWorksAnimations() {
  const worksSection = document.getElementById('works');
  if (!worksSection) return;

  const isMobile = window.matchMedia('(max-width: 768px)').matches;
  const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const isWorksPage = document.body.classList.contains('works-page');
  const showcase = document.getElementById('works-landing-showcase');

  if (isMobile || prefersReduced) {
    // On mobile, avoid ScrollTrigger-heavy reveal stacks and scrubbing parallax;
    // keep content visible and let native scrolling handle the rest.
    worksSection.classList.add('is-active');
    const show = (el) => el && gsap.set(el, { opacity: 1, y: 0, clearProps: 'transform' });
    show(worksSection.querySelector('.works-hero-label'));
    show(worksSection.querySelector('.works-hero-title'));
    show(worksSection.querySelector('.works-hero-description'));
    show(worksSection.querySelector('.works-hero-cta'));
    worksSection
      .querySelectorAll('.works-bento-title, .works-usecase-title, .works-bento-card, .works-usecase-card, .expandable-card-trigger')
      .forEach((el) => gsap.set(el, { opacity: 1, y: 0, clearProps: 'transform' }));
    return;
  }

  // /works uses Gallery4 carousels below the cinematic hero. GSAP batch + Lenis often
  // leaves laptop/desktop elements at opacity:0 when they are already in the viewport.
  if (isWorksPage && showcase) {
    worksSection.classList.add('is-active');
    revealShowcaseContent(showcase);
    return;
  }

  const heroLabel = worksSection.querySelector('.works-hero-label');
  const heroTitle = worksSection.querySelector('.works-hero-title');
  const heroDescription = worksSection.querySelector('.works-hero-description');

  if (heroLabel) {
    gsap.set(heroLabel, { opacity: 0, y: 20 });
    gsap.to(heroLabel, {
      opacity: 1,
      y: 0,
      duration: 0.8,
      ease: 'power3.out',
      scrollTrigger: {
        trigger: worksSection,
        start: 'top 80%',
        toggleActions: 'play none none reverse',
      },
    });
  }

  if (heroTitle) {
    gsap.set(heroTitle, { opacity: 0, y: 30 });
    gsap.to(heroTitle, {
      opacity: 1,
      y: 0,
      duration: 1,
      ease: 'power3.out',
      scrollTrigger: {
        trigger: worksSection,
        start: 'top 80%',
        toggleActions: 'play none none reverse',
      },
    });
  }

  if (heroDescription) {
    gsap.set(heroDescription, { opacity: 0, y: 30 });
    gsap.to(heroDescription, {
      opacity: 1,
      y: 0,
      duration: 1,
      delay: 0.2,
      ease: 'power3.out',
      scrollTrigger: {
        trigger: worksSection,
        start: 'top 78%',
        toggleActions: 'play none none reverse',
      },
    });
  }

  const heroCta = worksSection.querySelector('.works-hero-cta');
  if (heroCta) {
    gsap.set(heroCta, { opacity: 0, y: 30 });
    gsap.to(heroCta, {
      opacity: 1,
      y: 0,
      duration: 0.8,
      delay: 0.4,
      ease: 'power3.out',
      scrollTrigger: {
        trigger: worksSection,
        start: 'top 78%',
        toggleActions: 'play none none reverse',
      },
    });
  }

  const ctaSection = worksSection.querySelector('.works-cta-section');
  if (ctaSection) {
    gsap.set(ctaSection, { opacity: 0, y: 40 });
    gsap.to(ctaSection, {
      opacity: 1,
      y: 0,
      duration: 1,
      ease: 'power3.out',
      scrollTrigger: {
        trigger: ctaSection,
        start: 'top 85%',
        toggleActions: 'play none none reverse',
      },
    });
  }

  const bentoSections = worksSection.querySelectorAll('.works-bento-section, .works-usecase-section');
  if (bentoSections.length) {
    bentoSections.forEach((section) => {
      const title = section.querySelector('.works-bento-title, .works-usecase-title');
      const cards = section.querySelectorAll('.works-bento-card, .works-usecase-card, .expandable-card-trigger');
      const mediaImages = section.querySelectorAll('.expandable-card-image img');

      ScrollTrigger.create({
        trigger: section,
        start: 'top 60%',
        end: 'bottom 45%',
        onEnter: () => section.classList.add('is-active'),
        onEnterBack: () => section.classList.add('is-active'),
        onLeave: () => section.classList.remove('is-active'),
        onLeaveBack: () => section.classList.remove('is-active'),
      });

      if (title) {
        gsap.set(title, { opacity: 0, y: 30 });
        gsap.to(title, {
          opacity: 1,
          y: 0,
          duration: 1,
          ease: 'power3.out',
          scrollTrigger: {
            trigger: section,
            start: 'top 85%',
            toggleActions: 'play none none reverse',
          },
        });
      }
      if (cards.length) {
        const visibleCards = Array.from(cards).filter((card) => {
          const grid = card.closest('.works-usecase-grid');
          if (grid && grid.style.display === 'none') return false;
          return card.getClientRects().length > 0;
        });
        if (!visibleCards.length) return;

        gsap.set(visibleCards, { opacity: 0, y: 40 });
        ScrollTrigger.batch(visibleCards, {
          start: 'top 90%',
          end: 'bottom 10%',
          onEnter: (batch) => {
            gsap.to(batch, {
              opacity: 1,
              y: 0,
              duration: 0.8,
              ease: 'power3.out',
              stagger: 0.06,
            });
          },
          onLeaveBack: (batch) => {
            gsap.to(batch, {
              opacity: 0,
              y: 40,
              duration: 0.5,
              ease: 'power2.out',
            });
          },
        });
      }

      if (mediaImages.length) {
        mediaImages.forEach((img) => {
          gsap.fromTo(
            img,
            { yPercent: -6, scale: 1.06 },
            {
              yPercent: 6,
              scale: 1.12,
              ease: 'none',
              scrollTrigger: {
                trigger: img.closest('.expandable-card-trigger') || section,
                start: 'top bottom',
                end: 'bottom top',
                scrub: true,
              },
            },
          );
        });
      }
    });
    return;
  }

  const categorySections = worksSection.querySelectorAll('.works-section-category');
  categorySections.forEach((section) => {
    const title = section.querySelector('.works-category-title');
    const intro = section.querySelector('.works-category-intro');
    const grid = section.querySelector('.works-projects-grid');

    if (title) {
      gsap.set(title, { opacity: 0, y: 30 });
      gsap.to(title, {
        opacity: 1,
        y: 0,
        duration: 1,
        ease: 'power3.out',
        scrollTrigger: {
          trigger: section,
          start: 'top 85%',
          toggleActions: 'play none none reverse',
        },
      });
    }

    if (intro) {
      gsap.set(intro, { opacity: 0, y: 30 });
      gsap.to(intro, {
        opacity: 1,
        y: 0,
        duration: 1,
        delay: 0.1,
        ease: 'power3.out',
        scrollTrigger: {
          trigger: section,
          start: 'top 83%',
          toggleActions: 'play none none reverse',
        },
      });
    }

    if (grid) {
      const gridCards = grid.querySelectorAll('.project-card');
      if (gridCards.length) {
        gsap.set(gridCards, { opacity: 0, y: 40 });
        ScrollTrigger.batch(gridCards, {
          start: 'top 85%',
          end: 'bottom 10%',
          onEnter: (batch) => {
            gsap.to(batch, {
              opacity: 1,
              y: 0,
              duration: 0.8,
              ease: 'power3.out',
              stagger: 0.1,
            });
          },
          onLeaveBack: (batch) => {
            gsap.to(batch, {
              opacity: 0,
              y: 40,
              duration: 0.6,
              ease: 'power2.out',
            });
          },
        });
      }
    }
  });
}
